import type { CaptionsTranslationView, CaptionsView, OutputCaptionsView } from '../api';
import type { StateColor } from './ui';
import { t } from '../i18n';

/**
 * Captions on the Live screen (docs/captions-overlay.md §10.6): the API's
 * captions state turned into what the panel and the cards show. The video
 * never depends on them, and the texts say so where it matters.
 */

/** Language names the panel shows; codes it does not know show as the code. */
export const CAPTION_LANG: Record<string, string> = {
  en: 'English', es: 'Spanish', pt: 'Portuguese', 'pt-BR': 'Portuguese (Brazil)', de: 'German', fr: 'French', it: 'Italian',
  ru: 'Russian', uk: 'Ukrainian', pl: 'Polish', nl: 'Dutch', tr: 'Turkish', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', ar: 'Arabic', hi: 'Hindi',
};

export const langName = (code?: string) => (!code ? '' : code === 'auto' ? t('captions.auto') : CAPTION_LANG[code] ?? code.toUpperCase());

export interface CaptionsModel {
  /** short label of the state chip */
  label: string;
  color: StateColor;
  /** one line under the chip: what is happening and what to do */
  detail: string;
  /** "English · 2.1 s behind" while captions run */
  meta?: string;
}

/** Plan reasons of an off captions state (BILL-1 B3a): the stream goes on, its captions do not. */
export const CAPTIONS_PLAN_REASONS = ['plan_no_captions', 'plan_limit_captions_hours', 'plan_captions_overrun'] as const;

/**
 * The panel's model; nil view = captions off for this stream. planText: the text of a plan refusal
 * (billing.ts refusalText with the user's plan); a limit is warn, never fail (PlansAndLimits, UsageMeters).
 */
export function captionsModel(v: CaptionsView | undefined, streamLive: boolean, planText?: (code: string) => string | undefined): CaptionsModel {
  if (!v || v.state === 'off') {
    if (v?.reason === 'no_destination')
      return { label: t('captions.st.off'), color: 'warn', detail: t('captions.offNoDest') };
    if (v?.reason && (CAPTIONS_PLAN_REASONS as readonly string[]).includes(v.reason)) {
      const detail = planText?.(v.reason);
      if (detail) return { label: t('captions.st.off'), color: 'warn', detail };
    }
    return { label: t('captions.st.off'), color: 'idle', detail: v ? (streamLive ? t('captions.offStarting') : t('captions.offLater')) : t('captions.offPlain') };
  }
  const heard = v.detected_language ? langName(v.detected_language) : v.language === 'auto' ? t('captions.detecting') : langName(v.language);
  const lag = v.lag_ms !== undefined ? t('captions.behind', { s: (v.lag_ms / 1000).toFixed(1) }) : undefined;
  const meta = [heard, lag].filter(Boolean).join(' · ');
  switch (v.state) {
    case 'live':
      return { label: t('captions.st.live'), color: 'live', detail: v.language === 'auto' && !v.language_locked ? t('captions.listening') : t('captions.live'), meta };
    case 'starting':
      return { label: t('captions.st.starting'), color: 'start', detail: t('captions.starting') };
    case 'no_worker':
      return { label: t('captions.st.waiting'), color: 'warn', detail: t('captions.noWorker') };
    case 'rejected':
      return { label: t('captions.st.refused'), color: 'fail', detail: t('captions.refused'), meta };
    case 'stalled':
    default:
      return {
        label: t('captions.st.stalled'), color: 'warn',
        detail: v.reason === 'worker_lost' ? t('captions.workerLost')
          : v.reason === 'node_no_endpoint' || v.reason === 'node_no_captions' ? t('captions.nodeNo')
          : t('captions.stuck'),
        meta,
      };
  }
}

/** The chip on a destination card: the node's captions sender of that destination. */
export function outputCaptionsChip(c: OutputCaptionsView | undefined): { text: string; color: StateColor } | undefined {
  if (!c) return undefined;
  if (c.state === 'captions_rejected' || c.state === 'rejected') return { text: t('captions.chipRefused'), color: 'fail' };
  if (c.state === 'live') return { text: c.lag_ms !== undefined ? `CC · ${(c.lag_ms / 1000).toFixed(1)} s` : 'CC', color: 'live' };
  return { text: 'CC · ' + c.state.replace(/_/g, ' ').toUpperCase(), color: 'idle' };
}

/*
 * Translated captions (CAP-8, docs/captions-translation.md §9): the panel's
 * translation line and the language a YouTube output sends.
 */

/** The translation's chip and line under it; undefined while nothing is translated. */
export function translationModel(v: CaptionsTranslationView | undefined): CaptionsModel | undefined {
  if (!v) return undefined;
  const langs = v.languages.map(langName).join(', ');
  const p90 = Math.max(0, ...Object.values(v.targets ?? {}).map((x) => x.p90_ms ?? 0));
  const meta = p90 > 0 ? `${langs} · ${t('translate.p90', { s: (p90 / 1000).toFixed(1) })}` : langs;
  switch (v.state) {
    case 'live':
      return { label: t('captions.st.live'), color: 'live', detail: t('translate.live'), meta };
    case 'starting':
      return { label: t('captions.st.starting'), color: 'start', detail: t('translate.starting'), meta: langs };
    case 'degraded':
      return { label: t('translate.st.degraded'), color: 'warn', detail: t('translate.degraded'), meta };
    case 'no_capacity':
      return { label: t('captions.st.waiting'), color: 'warn', detail: t('translate.noCapacity'), meta: langs };
    case 'unavailable':
      return { label: t('translate.st.down'), color: 'fail', detail: v.reason === 'worker_no_translation' ? t('translate.oldWorker') : t('translate.down'), meta: langs };
    case 'off':
    default:
      return { label: t('captions.st.off'), color: v.reason ? 'warn' : 'idle', detail: v.reason === 'plan_no_caption_translation' ? t('translate.plan') : t('captions.offLater'), meta: langs };
  }
}

/** The phrases of a channel the viewer chose ("orig" = the original, whatever its language). */
export function recentPhrases(recent: Record<string, string[]> | undefined, view: string): string[] {
  if (!recent) return [];
  if (view !== 'orig') return recent[`translation/${view}`] ?? [];
  const orig = Object.entries(recent).filter(([ch]) => ch.startsWith('captions/'));
  // The newest original channel last spoken in (auto may switch languages).
  return orig.length ? orig[orig.length - 1]![1] : [];
}
